<template>
  <div class="rb-ob-form rb-match-page rb-book-page rb-pay-page rb-consent-page">
    <header><h1 class="rb-ob-title">{{ $t('intake.boundary02Title') }}</h1></header>
    <p class="rb-ready-hint">{{ $t('intake.boundary02Optional') }}</p>
    <p v-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>

    <fieldset v-for="item in scaleItems" :key="item.id" class="rb-ready-q">
      <legend>{{ item.text_fa }}</legend>
      <div class="rb-scale-row" role="radiogroup" :aria-label="item.text_fa">
        <label v-for="n in scaleValues" :key="item.id + '-' + n" :class="{'is-selected': answers[item.id] === n}">
          <input v-model.number="answers[item.id]" type="radio" :name="item.id" :value="n">
          <span>{{ digit(n) }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset v-if="supportItem" class="rb-ready-q">
      <legend>{{ supportItem.text_fa }}</legend>
      <div class="rb-choice-stack" role="radiogroup" :aria-label="supportItem.text_fa">
        <label v-for="opt in supportItem.options || []" :key="opt.id" :class="{'is-selected': answers.A4 === opt.id}">
          <input v-model="answers.A4" type="radio" name="A4" :value="opt.id">
          <span>{{ opt.label_fa }}</span>
        </label>
      </div>
    </fieldset>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit" type="button" :disabled="loading || !hasAnyAnswer || submitting || !care || !questionBank || !questionBank.version" @click="submit(false)">
        {{ submitting ? $t('intake.portal.loading') : $t('intake.boundary02Submit') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="loading || submitting || !care" @click="later">
        {{ $t('intake.boundary02Later') }}
      </button>
      <button v-if="!loading && (!care || !questionBank || !questionBank.version)" class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="submitting" @click="load">
        {{ $t('intake.boundary02Retry') }}
      </button>
      <router-link v-if="!loading && !care" class="rb-ob-submit rb-ob-submit-secondary" :to="{name:'portalHome',params:{lang:lang}}">
        {{ $t('intake.boundary02ReturnHome') }}
      </router-link>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust"><p>{{ $t('intake.trustPath') }}</p><p>{{ $t('authUi.familyNote') }}</p></div>
    </div>
  </div>
</template>

<script>
import {toFaDigits} from '@/intake/intakeState'
import {clientMessage, clientError} from '@/intake/clientMessages'
import {fetchActiveCare, saveCareEngagementBaseline} from '@/intake/portalApi'

export default {
  name: 'MatchBaselineStep',
  data() {
    return {care:null, questionBank:null, answers:{A1:null,A2:null,A3:null,A4:null}, scaleValues:[0,1,2,3,4,5,6,7,8,9,10], formStatus:'', loading:true, submitting:false, loadGeneration:0}
  },
  computed: {
    lang() { return this.$route.params.lang || 'fa' },
    scaleItems() { return this.questionBank ? (this.questionBank.items || []).filter((x) => x.type === 'INTEGER_SCALE') : [] },
    supportItem() { return this.questionBank ? (this.questionBank.items || []).find((x) => x.id === 'A4') : null },
    hasAnyAnswer() { return Object.keys(this.answers).some((key) => this.answers[key] !== null && this.answers[key] !== '') },
    allAnswered() { return ['A1','A2','A3','A4'].every((key) => this.answers[key] !== null && this.answers[key] !== '') },
  },
  mounted() {
    return this.load()
  },
  watch: {'$route.fullPath'() { this.load() }},
  beforeDestroy() { this.loadGeneration++ },
  methods: {
    load() {
      const generation = ++this.loadGeneration
      this.loading = true; this.formStatus = ''; this.care = null; this.questionBank = null
      this.answers = {A1:null,A2:null,A3:null,A4:null}
      return fetchActiveCare(this.$route.query || {}).then((care) => {
        if (generation !== this.loadGeneration) return
        this.care = care
        this.questionBank = care && care.engagement_baseline ? care.engagement_baseline.question_bank : null
        this.applySavedAnswers(care && care.engagement_baseline ? care.engagement_baseline.answers : null)
        if (!care) this.formStatus = clientMessage('CARE_CONTEXT_REQUIRED', this.lang)
        else if (!this.questionBank || !this.questionBank.version) this.formStatus = clientMessage('ENGAGEMENT_QUESTION_BANK_UNAVAILABLE', this.lang)
      }).catch((e) => { if (generation === this.loadGeneration) this.formStatus = clientError(e, this.lang) })
        .finally(() => { if (generation === this.loadGeneration) this.loading = false })
    },
    digit(n) { return this.$i18n.locale === 'fa' ? toFaDigits(String(n)) : String(n) },
    applySavedAnswers(saved) {
      if (!saved || typeof saved !== 'object') return
      ;['A1', 'A2', 'A3'].forEach((id) => {
        if (saved[id] === null || saved[id] === undefined || saved[id] === '') return
        const n = Number(saved[id])
        if (Number.isInteger(n) && n >= 0 && n <= 10) this.answers[id] = n
      })
      if (['SEVERAL', 'ONE', 'NONE'].indexOf(saved.A4) !== -1) this.answers.A4 = saved.A4
    },
    goNext() { this.$router.push({name:'intakeLetter',params:{lang:this.$route.params.lang||'fa'},query:this.$route.query}) },
    submit(complete) {
      if (this.loading || this.submitting || !this.care || !this.questionBank || !this.questionBank.version || !this.hasAnyAnswer) return
      const clean = {}
      Object.keys(this.answers).forEach((id) => { if (this.answers[id] !== null && this.answers[id] !== '') clean[id] = this.answers[id] })
      this.submitting = true; this.formStatus = ''
      const generation = this.loadGeneration
      return saveCareEngagementBaseline(this.care.id, {question_bank_version:this.questionBank.version, answers:clean, complete:!!complete})
        .then(() => { if (generation === this.loadGeneration) this.goNext() })
        .catch((e) => { if (generation === this.loadGeneration) this.formStatus = clientError(e, this.lang) })
        .finally(() => { this.submitting = false })
    },
    later() {
      if (this.loading || this.submitting || !this.care) return
      this.submitting=true; this.formStatus=''
      const generation = this.loadGeneration
      return saveCareEngagementBaseline(this.care.id,{skip:true}).then(() => { if (generation === this.loadGeneration) this.goNext() })
        .catch((e) => { if (generation === this.loadGeneration) this.formStatus=clientError(e,this.lang) })
        .finally(() => { this.submitting=false })
    },
  },
}
</script>
